import { ICommand } from '../ICommand';
import { Node, Edge } from '../../models/scene';
import type * as Y from 'yjs';

/**
 * Represents a recorded change for undo/redo
 */
export interface NodeChangeRecord {
  nodeId: string;
  oldState: Partial<Node>;
  newState: Partial<Node>;
  /** Coordinate-only history belongs to this presentation document, not the current model. */
  projectedLayoutDoc?: Y.Doc;
}

/**
 * Represents a recorded edge change for undo/redo
 */
export interface EdgeChangeRecord {
  edgeId: string;
  oldState: Partial<Edge> | null;
  newState: Partial<Edge>;
}

/**
 * Interface for managing state mutations in a reversible way
 */
export interface INodeMutationManager {
  applyNodeChange(nodeId: string, changes: Partial<Node>): NodeChangeRecord;
  revertNodeChange(record: NodeChangeRecord): void;
  getNode(nodeId: string): Node | undefined;
  updateEdge(edgeId: string, updates: Partial<Edge>): EdgeChangeRecord;
  restoreEdge(record: EdgeChangeRecord): void;
  restoreProjectedPlacement?(record: NodeChangeRecord, undo: boolean): void;
}

/**
 * Base class for commands that modify nodes
 * Ensures all mutations are reversible and go through proper channels
 */
export abstract class BaseNodeCommand implements ICommand {
  id: string;
  name: string;
  description?: string;
  timestamp: number;
  
  protected mutationManager: INodeMutationManager;
  protected changeRecords: NodeChangeRecord[] = [];
  
  constructor(
    name: string,
    description?: string
  ) {
    this.id = `${name.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}`;
    this.name = name;
    this.description = description;
    this.timestamp = Date.now();
    // Mutation manager will be set lazily on first access
    this.mutationManager = null as any;
  }
  
  /**
   * Get the mutation manager (lazy initialization to avoid circular deps)
   */
  protected getMutationManager(): INodeMutationManager {
    if (!this.mutationManager) {
      // Use a simple singleton pattern without importing to avoid circular deps
      // The NodeMutationManager will register itself when imported
      this.mutationManager = (window as any).__nodeMutationManager;
      if (!this.mutationManager) {
        throw new Error('NodeMutationManager not initialized. Make sure NodeMutationManager is imported before using commands.');
      }
    }
    return this.mutationManager;
  }
  
  /**
   * Execute the command and record changes for undo
   */
  execute(): void {
    this.changeRecords = this.doExecute();
  }
  
  /**
   * Undo the command by reverting recorded changes
   */
  undo(): void {
    // Revert changes in reverse order
    [...this.changeRecords].reverse().forEach(record => 
      this.getMutationManager().revertNodeChange(record)
    );
  }
  
  /**
   * Redo is typically the same as execute
   */
  redo(): void {
    const manager = this.getMutationManager();
    if (this.changeRecords.length > 0 && this.changeRecords.every((record) => record.projectedLayoutDoc)
      && manager.restoreProjectedPlacement) {
      this.changeRecords.forEach((record) => manager.restoreProjectedPlacement!(record, false));
      return;
    }
    this.changeRecords = this.doExecute();
  }
  
  /**
   * Subclasses implement the actual mutation logic
   * @returns Array of change records for undo support
   */
  protected abstract doExecute(): NodeChangeRecord[];
  
  /**
   * Check if this command can merge with another
   */
  canMergeWith(_other: ICommand): boolean {
    return false; // Override in subclasses for mergeable commands
  }
  
  /**
   * Merge this command with another
   */
  mergeWith(_other: ICommand): ICommand {
    throw new Error('Merging not supported for this command type');
  }
}
